import { SxProps, Theme } from '@mui/material';
import {
  FILTER_COLUMN_AUTOCOMPLETE_CLASS,
  FILTER_COLUMN_LISTBOX_MAX_HEIGHT,
  FILTER_DATE_RANGE_CLASS,
} from '../filterPanel/constants';
import { Color } from 'shared/utils/colors';

const FILTER_INPUT_HEIGHT_VALUE = '40px';
const FILTER_INPUT_HEIGHT = `${FILTER_INPUT_HEIGHT_VALUE} !important`;
const FILTER_INPUT_PADDING = '8.5px 14px !important';
const FILTER_COLUMN_INPUT_WIDTH = 170;
const FILTER_OPERATOR_INPUT_WIDTH = 120;
const FILTER_VALUE_INPUT_MIN_WIDTH = 200;
const FILTER_DATE_RANGE_INPUT_MIN_WIDTH = 160;

const FILTER_PANEL_MIN_WIDTH = 860;
const FILTER_PANEL_MAX_WIDTH = 960;
const FILTER_CONTENT_MAX_HEIGHT = 220;

const PRESETS_SIDEBAR_WIDTH = 220;
const PRESETS_SIDEBAR_COLLAPSED_WIDTH = 48;
const PRESETS_LIST_MAX_HEIGHT = 180;

const CREATE_PRESET_DIALOG_WIDTH = 560;

const filterInputControlSx = {
  height: FILTER_INPUT_HEIGHT,
  maxHeight: FILTER_INPUT_HEIGHT,
  width: '100%',
  margin: '0 !important',
  '& .MuiInputBase-root': {
    height: FILTER_INPUT_HEIGHT,
    minHeight: FILTER_INPUT_HEIGHT,
    maxHeight: FILTER_INPUT_HEIGHT,
  },
  '& .MuiInputBase-input': {
    height: FILTER_INPUT_HEIGHT,
    minHeight: FILTER_INPUT_HEIGHT,
    maxHeight: FILTER_INPUT_HEIGHT,
    padding: FILTER_INPUT_PADDING,
  },
  '& .MuiOutlinedInput-root': {
    height: FILTER_INPUT_HEIGHT,
    maxHeight: FILTER_INPUT_HEIGHT,
  },
  '& .MuiSelect-root': {
    height: FILTER_INPUT_HEIGHT,
    padding: FILTER_INPUT_PADDING,
  },
};

const filterInputContainerSx = {
  height: FILTER_INPUT_HEIGHT,
  maxHeight: FILTER_INPUT_HEIGHT,
};

const filterFormFieldLabelSx = {
  '& .MuiInputLabel-root': {
    transform: 'translate(14px, 9px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
};

const filterFormAlignedContainerSx = {
  display: 'flex',
  alignItems: 'center',
  height: FILTER_INPUT_HEIGHT,
  maxHeight: FILTER_INPUT_HEIGHT,
  ...filterFormFieldLabelSx,
  '& .MuiFormControl-root': {
    margin: '0 !important',
    width: '100%',
  },
};

export const gridFilterPanelSxProps: SxProps<Theme> = {
  '& .MuiDataGrid-panelWrapper': {
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
  },
  '& .MuiDataGrid-panelContent': {
    mt: 0,
    minWidth: 0,
    padding: '12px 16px !important',
  },
  '& .MuiMultiInputDateRangeField-separator': {
    display: 'none !important',
    width: '0 !important',
    margin: '0 !important',
    padding: '0 !important',
  },
  '& .MuiDataGrid-filterForm': {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 1,
    flexShrink: 0,
    width: '100%',
  },
  '& .MuiDataGrid-filterFormColumnInput': {
    flex: `0 1 ${FILTER_COLUMN_INPUT_WIDTH}px !important`,
    minWidth: `${FILTER_COLUMN_INPUT_WIDTH}px !important`,
    maxWidth: 'none !important',
    width: `${FILTER_COLUMN_INPUT_WIDTH}px`,
    ...filterFormAlignedContainerSx,
    [`& .${FILTER_COLUMN_AUTOCOMPLETE_CLASS}`]: {
      width: '100%',
      minWidth: `${FILTER_COLUMN_INPUT_WIDTH}px`,
      display: 'flex',
      alignItems: 'center',
      height: FILTER_INPUT_HEIGHT,
      maxHeight: FILTER_INPUT_HEIGHT,
      '& .MuiFormControl-root': {
        height: FILTER_INPUT_HEIGHT_VALUE,
        width: '100%',
        margin: '0 !important',
        minWidth: `${FILTER_COLUMN_INPUT_WIDTH}px`,
      },
      '& .MuiAutocomplete-inputRoot': {
        height: FILTER_INPUT_HEIGHT_VALUE,
        minHeight: FILTER_INPUT_HEIGHT_VALUE,
        maxHeight: FILTER_INPUT_HEIGHT_VALUE,
        paddingRight: '32px !important',
      },
      '& .MuiAutocomplete-inputRoot .MuiInputBase-input': {
        color: `${Color.black} !important`,
        height: 'auto !important',
        minHeight: 'unset !important',
        maxHeight: 'unset !important',
        padding: '8.5px 4px !important',
      },
      '& .MuiAutocomplete-endAdornment': {
        display: 'flex !important',
        alignItems: 'center',
        height: '100%',
        maxHeight: FILTER_INPUT_HEIGHT_VALUE,
        right: '7px',
        visibility: 'visible',
        opacity: 1,
      },
      '& .MuiAutocomplete-popupIndicator': {
        display: 'flex !important',
        visibility: 'visible !important',
        opacity: '1 !important',
        padding: '2px',
        '& .MuiSvgIcon-root': {
          visibility: 'visible !important',
          opacity: '1 !important',
          color: Color.iconGray,
        },
      },
      '& .MuiAutocomplete-listbox': {
        maxHeight: FILTER_COLUMN_LISTBOX_MAX_HEIGHT,
      },
    },
  },
  '& .MuiDataGrid-filterFormOperatorInput': {
    flex: `0 1 ${FILTER_OPERATOR_INPUT_WIDTH}px !important`,
    minWidth: `${FILTER_OPERATOR_INPUT_WIDTH}px !important`,
    maxWidth: 'none !important',
    width: `${FILTER_OPERATOR_INPUT_WIDTH}px`,
    ...filterFormAlignedContainerSx,
    '& .MuiFormControl-root': {
      ...filterInputControlSx,
      minWidth: `${FILTER_OPERATOR_INPUT_WIDTH}px`,
    },
  },
  '& .MuiDataGrid-filterFormValueInput': {
    flex: '1 1 0% !important',
    width: 'auto !important',
    minWidth: `${FILTER_VALUE_INPUT_MIN_WIDTH}px`,
    maxWidth: 'none',
    // DateRangeFilterInput renders FILTER_DATE_RANGE_CLASS as a direct child of this slot.
    [`& > *:not(.${FILTER_DATE_RANGE_CLASS})`]: {
      display: 'block',
      width: '100%',
      minWidth: 0,
    },
    '& .MuiTextField-root': {
      width: '100%',
    },
    '& .MuiFormControl-root': {
      height: FILTER_INPUT_HEIGHT_VALUE,
      width: '100%',
    },
    '& .MuiInputBase-root': {
      height: FILTER_INPUT_HEIGHT_VALUE,
    },
    [`& .${FILTER_DATE_RANGE_CLASS}`]: {
      display: 'flex',
      width: '100%',
      minWidth: 0,
      alignItems: 'center',
      '& .MuiMultiInputDateRangeField-root': {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        width: '100%',
        minWidth: 0,
        gap: '8px',
      },
      '& .MuiFormControl-root, & .MuiTextField-root': {
        flex: '1 1 0',
        minWidth: `${FILTER_DATE_RANGE_INPUT_MIN_WIDTH}px`,
        width: 'auto',
        height: FILTER_INPUT_HEIGHT_VALUE,
        margin: '0 !important',
        '& .MuiInputBase-root': {
          height: FILTER_INPUT_HEIGHT_VALUE,
          minHeight: FILTER_INPUT_HEIGHT_VALUE,
        },
        '& .MuiInputBase-input': {
          padding: FILTER_INPUT_PADDING,
          lineHeight: '1.5',
        },
        '& .MuiInputLabel-root': {
          transform: 'translate(14px, 9px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      },
    },
    '& .MuiAutocomplete-root': {
      width: '100%',
      '& .MuiFormControl-root': {
        height: 'auto',
        minHeight: FILTER_INPUT_HEIGHT_VALUE,
      },
      '& .MuiAutocomplete-inputRoot': {
        minHeight: FILTER_INPUT_HEIGHT_VALUE,
        paddingRight: '32px !important',
      },
      '& .MuiAutocomplete-endAdornment': {
        display: 'flex',
        alignItems: 'center',
        right: '7px',
      },
    },
  },
  '& .MuiDataGrid-filterFormAdd, & .MuiDataGrid-filterFormDeleteAll': {
    position: 'sticky',
    bottom: 0,
    zIndex: 2,
    backgroundColor: 'background.paper',
    py: 1,
  },
  '& .MuiDataGrid-filterFormAdd:hover, & .MuiDataGrid-filterFormDeleteAll:hover': {
    backgroundColor: Color.primaryOrangeHover,
  },
  '& .MuiDataGrid-panelFooter': {
    padding: '12px 16px !important',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  },
  '& .MuiDataGrid-filterFormAdd .MuiButton-startIcon': {
    marginRight: 10,
    marginLeft: 0,
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: 'text.primary',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiDataGrid-filterFormAdd .MuiButton-startIcon .MuiSvgIcon-root': {
    width: 16,
    height: 16,
    color: 'background.paper',
    display: 'block',
  },
};

export const filterPanelLayoutSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'stretch',
  minWidth: FILTER_PANEL_MIN_WIDTH,
  maxWidth: FILTER_PANEL_MAX_WIDTH,
};

export const filterPresetsSidebarExpandedSx: SxProps<Theme> = {
  width: PRESETS_SIDEBAR_WIDTH,
  flexShrink: 0,
  borderRight: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  transition: 'width 0.2s ease-in-out',
  overflow: 'hidden',
  '& .MuiIconButton-root:hover': {
    backgroundColor: Color.primaryOrangeHover,
  },
};

export const filterPresetsSidebarCollapsedSx: SxProps<Theme> = {
  width: PRESETS_SIDEBAR_COLLAPSED_WIDTH,
  flexShrink: 0,
  borderRight: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  transition: 'width 0.2s ease-in-out',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  '& .MuiIconButton-root:hover': {
    backgroundColor: Color.primaryOrangeHover,
  },
};

export const filterPresetsHeaderExpandedSx: SxProps<Theme> = {
  pl: 2,
  pr: 1,
  py: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 40,
  transition: 'padding 0.2s ease-in-out',
};

export const filterPresetsHeaderCollapsedSx: SxProps<Theme> = {
  pt: 1,
  px: 1,
  pb: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 40,
  transition: 'padding 0.2s ease-in-out',
  '& .MuiIconButton-root': {
    flexShrink: 0,
  },
};

export const filterPresetsContentExpandedSx: SxProps<Theme> = {
  opacity: 1,
  visibility: 'visible',
  flex: 1,
  minWidth: 0,
  transition: 'opacity 0.15s ease-in-out 0.05s, visibility 0.15s ease-in-out 0.05s',
};

export const filterPresetsContentCollapsedSx: SxProps<Theme> = {
  opacity: 0,
  visibility: 'hidden',
  width: 0,
  minWidth: 0,
  maxWidth: 0,
  overflow: 'hidden',
  flex: '0 0 0',
  transition: 'opacity 0.1s ease-in-out, visibility 0.1s ease-in-out, width 0.2s ease-in-out',
};

export const filterPresetsHeaderActionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
};

export const filterPresetsListExpandedSx: SxProps<Theme> = {
  px: 1,
  pb: 1,
  maxHeight: PRESETS_LIST_MAX_HEIGHT,
  overflowY: 'auto',
  opacity: 1,
  visibility: 'visible',
  transition: 'opacity 0.15s ease-in-out 0.05s, visibility 0.15s ease-in-out 0.05s',
};

export const filterPresetsListCollapsedSx: SxProps<Theme> = {
  px: 1,
  pb: 0,
  height: 0,
  maxHeight: 0,
  overflow: 'hidden',
  opacity: 0,
  visibility: 'hidden',
  transition: 'opacity 0.1s ease-in-out, visibility 0.1s ease-in-out',
};

export const filterPresetItemSx = (selected: boolean, isStatic?: boolean): SxProps<Theme> => ({
  pl: 1.5,
  pr: 1.5,
  py: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  borderRadius: 1,
  position: 'relative',
  bgcolor: selected ? 'action.selected' : isStatic ? 'grey.50' : 'transparent',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: selected ? 'primary.main' : 'transparent',
  },
  '&:hover': {
    bgcolor: selected ? 'action.selected' : 'action.hover',
  },
  '& + &': {
    mt: 0.5,
  },
});

export const filterPresetItemTextSx = (selected: boolean): SxProps<Theme> => ({
  flex: 1,
  minWidth: 0,
  mr: 1,
  '& .MuiTypography-body2': {
    fontWeight: selected ? 700 : 400,
  },
});

export const filterPresetItemActionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

export const filterPresetsEmptySx: SxProps<Theme> = {
  mx: 1,
  mt: 0.5,
  p: 2,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  color: 'text.secondary',
  textAlign: 'center',
};

export const filterPresetsEmptyStateSx: SxProps<Theme> = {
  px: 2,
  py: 3,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
};

export const filterPanelFormsAreaSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  px: 0,
  py: 0,
  display: 'flex',
  flexDirection: 'column',
  '& .MuiDataGrid-panelWrapper': {
    display: 'flex',
    flexDirection: 'column',
  },
  '& .MuiDataGrid-panelContent': {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    maxHeight: FILTER_CONTENT_MAX_HEIGHT,
    padding: '12px 16px !important',
  },
  '& .MuiDataGrid-panelFooter': {
    flexShrink: 0,
  },
};

export const filterPanelFormsAreaReadonlySx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  px: 0,
  py: 0,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  '& .MuiDataGrid-panelWrapper': {
    display: 'flex',
    flexDirection: 'column',
  },
  '& .MuiDataGrid-panelContent': {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    maxHeight: FILTER_CONTENT_MAX_HEIGHT,
    padding: '12px 16px !important',
  },
  '& .MuiDataGrid-panelFooter': {
    flexShrink: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Color.whiteOverlay60,
    pointerEvents: 'none',
    zIndex: 1,
  },
  '& .MuiDataGrid-filterFormAdd, & .MuiDataGrid-filterFormDeleteAll': {
    display: 'none !important',
  },
  '& .MuiDataGrid-filterFormDeleteIcon': {
    display: 'none !important',
  },
  pointerEvents: 'none',
};

export const createPresetDialogActionsSx: SxProps<Theme> = {
  justifyContent: 'flex-end',
  px: 3,
  py: 2,
  gap: 1,
};

export const createPresetDialogPaperSx: SxProps<Theme> = {
  width: CREATE_PRESET_DIALOG_WIDTH,
  maxWidth: 'calc(100vw - 32px)',
};

export const createPresetDialogTitleSx: SxProps<Theme> = {
  px: 3,
  pt: 2.5,
  pb: 1,
  fontWeight: 600,
  fontSize: '1.125rem',
};

export const createPresetDialogContentSx: SxProps<Theme> = {
  px: 3,
  pb: 2,
};

export const filterPanelFormsHeaderSx: SxProps<Theme> = {
  px: 2,
  py: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 40,
};

export const filterPanelFormsHeaderTitleSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
  flex: 1,
};

export const filterPanelFormsHeaderActionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  flexShrink: 0,
};
